import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { deletePopulerCity, getCities, getPopulerCities } from '../../../../../api/endpoints/cmsContent';
import type { City, PopulerCity } from '../../../../../api/types/cmsContent';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../../components/common';
import { PopulerCityFormDialog } from '../PopulerCityFormDialog';

/** `SiteDetailPage` içinde bir sekme; `siteId` sabit prop. Aynı sitenin `City` listesini
 *  de çeker — `city_Id` → ad çözümü + form diyaloğunun şehir seçicisi için. */
export default function PopulerCityListPage({ siteId }: { siteId: number }) {
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<PopulerCity | null>(null);

  const query = useQuery({
    queryKey: ['cms', 'content', 'populerCities', siteId],
    queryFn: () => getPopulerCities(siteId),
  });
  const citiesQuery = useQuery({ queryKey: ['cms', 'content', 'cities', siteId], queryFn: () => getCities(siteId) });
  const cities: City[] = useMemo(() => citiesQuery.data?.cities ?? [], [citiesQuery.data]);
  const cityNameById = useMemo(
    () => new Map(cities.map((c) => [c.id, c.name ?? c.displayName ?? `#${c.id}`])),
    [cities]
  );

  const rows: PopulerCity[] = useMemo(() => query.data?.populerCities ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.title, row.cityCode, row.link]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const del = useDeleteResource<number>({
    mutationFn: (id) => deletePopulerCity(id),
    invalidateKeys: [['cms', 'content', 'populerCities']],
    successMessage: 'Popüler şehir silindi.',
  });
  const deletingName = rows.find((r) => r.id === del.target)?.title ?? '';

  const columns: DataTableColumn<PopulerCity>[] = [
    { id: 'title', header: 'Başlık', cell: (r) => r.title ?? '—' },
    { id: 'city', header: 'Şehir', cell: (r) => (r.city_Id ? cityNameById.get(r.city_Id) ?? `#${r.city_Id}` : '—') },
    {
      id: 'price',
      header: 'Fiyat',
      cell: (r) => (r.price !== undefined ? `${r.price} ${r.priceCurrency ?? ''}`.trim() : '—'),
    },
    { id: 'priority', header: 'Öncelik', cell: (r) => r.priority ?? '—' },
  ];

  return (
    <ListPageScaffold
      title="Popüler Şehirler"
      headerVariant="section"
      createLabel="Yeni Popüler Şehir"
      onCreate={() => setCreateOpen(true)}
      search={{ value: search, onChange: setSearch, placeholder: 'Başlık, kod, link...' }}
      query={query}
      rows={filteredRows}
      columns={columns}
      getRowKey={(r) => r.id}
      emptyTitle={rows.length === 0 ? 'Kayıtlı popüler şehir bulunamadı.' : 'Aramayla eşleşen kayıt bulunamadı.'}
      rowActions={(r) => <RowActions onEdit={() => setEditing(r)} onDelete={() => del.request(r.id)} />}
    >
      <PopulerCityFormDialog open={createOpen} populerCity={null} cities={cities} siteId={siteId} onClose={() => setCreateOpen(false)} />
      <PopulerCityFormDialog open={!!editing} populerCity={editing} cities={cities} siteId={siteId} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Popüler şehri sil"
        description={`"${deletingName}" kaydını silmek istediğinize emin misiniz?`}
      />
    </ListPageScaffold>
  );
}
